import React, { useEffect, useState } from 'react';
import { useCollectionStore } from '../../lib/collectionStore';
import { getAllSets } from '../../services/pokemonTcgApi';
import { Link } from 'react-router-dom';
import { slugify } from '../../utils/slugify';
import { CircularProgress } from '../../components/CircularProgress';
import { ChevronDown, ChevronRight } from 'lucide-react';

export default function SeriesView() {
  const { collection } = useCollectionStore();
  const [allSets, setAllSets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSeries, setExpandedSeries] = useState<string[]>(['scarlet-violet']);
  const [sortBy, setSortBy] = useState<'name' | 'completion' | 'cards'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const sets = await getAllSets();
        setAllSets(sets);
      } catch (error) {
        console.error('Error fetching sets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSets();
  }, []);

  // Group cards by series and set
  const seriesGroups = allSets.reduce((groups, set) => {
    const seriesName = set.series;
    const seriesId = slugify(seriesName);
    
    if (!groups[seriesId]) {
      groups[seriesId] = {
        id: seriesId,
        name: seriesName,
        sets: [],
        totalCards: 0,
        collectedCards: 0
      };
    }
    
    // Find cards in collection for this set
    const setCards = collection.filter(card => card.setId === set.id);
    const uniqueCardsInSet = setCards.length;
    
    groups[seriesId].sets.push({
      ...set,
      collectedCards: uniqueCardsInSet,
      completion: (uniqueCardsInSet / set.total) * 100
    });
    
    groups[seriesId].totalCards += set.total;
    groups[seriesId].collectedCards += uniqueCardsInSet;
    
    return groups;
  }, {} as Record<string, any>);

  // Sort series
  const sortedSeries = Object.values(seriesGroups).sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'completion':
        const completionA = (a.collectedCards / a.totalCards) * 100;
        const completionB = (b.collectedCards / b.totalCards) * 100;
        comparison = completionA - completionB;
        break;
      case 'cards':
        comparison = a.collectedCards - b.collectedCards;
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const toggleSeries = (seriesId: string) => {
    setExpandedSeries(prev => 
      prev.includes(seriesId) 
        ? prev.filter(s => s !== seriesId)
        : [...prev, seriesId]
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Series Overview</h2>
            <p className="text-white/60">
              View your collection organized by Pokémon TCG series.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'completion' | 'cards')}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="name">Sort by Name</option>
              <option value="completion">Sort by Completion</option>
              <option value="cards">Sort by Cards</option>
            </select>
            <button
              onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 bg-white/5 border border-white/10 rounded-lg"
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {sortedSeries.map(series => {
            const completion = (series.collectedCards / series.totalCards) * 100;
            const isExpanded = expandedSeries.includes(series.id);

            return (
              <div
                key={series.id}
                className="group relative bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden hover:bg-white/5 transition-colors isolate"
              >
                <button
                  onClick={() => toggleSeries(series.id)} 
                  className="relative w-full p-4 text-left"
                >
                  <div className="flex items-center gap-4">
                    <CircularProgress 
                      value={completion} 
                      size={40}
                      strokeWidth={4}
                      className="text-green-500"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium group-hover:text-white/90">
                        {series.name}
                      </h3>
                      <p className="text-sm text-white/60">
                        {series.collectedCards} / {series.totalCards} cards
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="text-white/60" size={20} />
                    ) : (
                      <ChevronRight className="text-white/60" size={20} />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="relative border-t border-white/10 p-4 bg-black/40 backdrop-blur-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {series.sets.map(set => (
                        <Link
                          key={set.id}
                          to={`/${slugify(series.name)}/${slugify(set.name)}`}
                          className="relative bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-colors isolate group"
                        >
                          {/* Set Logo Background */}
                          {set.images?.logo && (
                            <div className="absolute inset-0 opacity-25 -z-10">
                              <img
                                src={set.images.logo}
                                alt=""
                                className="w-full h-full object-contain opacity-40 scale-75"
                                loading="lazy"
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <CircularProgress 
                              value={set.completion} 
                              size={32}
                              strokeWidth={3}
                              className="text-green-500"
                            />
                            <div>
                              <h4 className="font-medium group-hover:text-white/90">{set.name}</h4>
                              <p className="text-sm text-white/60">
                                {set.collectedCards} / {set.total} cards
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}